// AUTH-01 Sign in.
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Button, Form } from "react-aria-components";
import { api, call } from "../../api/client";
import { describeError } from "../../api/errors";
import { session, type AuthSession } from "../../api/session";
import { Banner } from "../../components/Banner";
import { AuthLayout } from "./AuthLayout";
import { Field } from "./fields";
import { mfaChallenge } from "./mfaChallenge";

/** Only same-app paths: never another origin or a protocol-relative URL. */
export function safeRedirect(target: string | undefined): string {
  return target && target.startsWith("/") && !target.startsWith("//") && !target.startsWith("/\\") ? target : "/";
}

export function SignIn() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: string; reset?: string; ended?: string };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await call(api.POST("/auth/login", { body: { email, password } }));
      if ("mfa_required" in res && res.mfa_required) {
        mfaChallenge.set({ mfa_token: res.mfa_token, methods: res.methods, expiresAt: Date.now() + res.expires_in * 1000, redirect: safeRedirect(search.redirect) });
        void navigate({ to: "/sign-in/mfa" });
        return;
      }
      await session.start(res as AuthSession);
      void navigate({ to: safeRedirect(search.redirect) });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          <Link to="/forgot-password">Forgot password, or never set one?</Link>
          <Link to="/sign-up">Create an account</Link>
        </>
      }
    >
      {search.reset && <Banner>Your password is set. Sign in with it now.</Banner>}
      {search.ended && <Banner tone="warning">Your session ended. Sign in again.</Banner>}
      {error && <Banner tone="critical">{error}</Banner>}
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Email" type="email" name="email" autoComplete="username" isRequired value={email} onChange={setEmail} autoFocus />
        <Field label="Password" type="password" name="password" autoComplete="current-password" isRequired value={password} onChange={setPassword} />
        <Button type="submit" className="button primary wide" isPending={busy}>
          Sign in
        </Button>
      </Form>
    </AuthLayout>
  );
}
